import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { userIdentityShape, catalogBibShape, branchShape, libraryShape } from '@bibliocommons/bc-prop-types';
import { TransactionalSolidButton } from '@bibliocommons/base-buttons';

import TextArea from 'app/components/shared/TextArea';
import { ModalHeader, ModalContent, ModalFooter } from '@bibliocommons/modal';

import Checkbox from 'app/components/shared/Checkbox';

import { BibBriefContainer } from 'app/components/bibs/shared/BibBrief';
import PickupBranchSelect from 'app/components/bibs/shared/PickupBranchSelect';
import accountShape from 'app/shapes/accountShape';

import './LiteSuggestionForm.scss';

const NOTES_MAX_LENGTH = 1000;

export default function LiteSuggestionForm({
  currentLibrary,
  branches,
  preferredBranches,
  currentAccount,
  handleClose,
  suggestForPurchaseActions,
  suggestForPurchaseTransactions,
  currentUser,
  catalogBib
}) {
  const [notes, setNotes] = useState('');
  const [placeHold, setPlaceHold] = useState(false);
  const [branch, setBranch] = useState(null);
  const metadataId = catalogBib.get('metadataId');
  const userId = currentUser.get('id');
  const holdEnabled = currentLibrary.getIn(['suggestForPurchase', 'lite', 'holdsOnSuggestedItems']);

  function handleSubmit(e) {
    e?.preventDefault(); // eslint-disable-line babel/no-unused-expressions

    suggestForPurchaseActions.submitLiteSuggestion({
      suggestion: {
        notes,
        branch: placeHold ? branch : null,
        metadataId,
        userId
      },
      postSuccess: handleClose
    });
  }

  function isFormInvalid() {
    return !notes || (placeHold && !branch);
  }

  function renderSuggestionBox() {
    return (
      <TextArea
        className="lite-suggestion-form__suggestion-box"
        dataKey="notes-textbox"
        id="lite-suggestion-notes-textbox"
        label={<FormattedMessage id="suggest_for_purchase_suggestion_box" />}
        maxLength={NOTES_MAX_LENGTH}
        minHeight="9rem"
        handleChange={setNotes}
        required
        value={notes}
      />
    );
  }

  function renderPlaceHoldCheckbox() {
    return (
      <Checkbox
        id="place-hold-checkbox"
        dataKey="place-hold-checkbox"
        label={<FormattedMessage id="suggest_for_purchase_place_hold" />}
        checked={placeHold}
        handleChange={() => setPlaceHold(!placeHold)}
      />
    );
  }

  function renderBranchSelect() {
    return placeHold ? (
      <PickupBranchSelect
        branches={branches}
        preferredBranches={preferredBranches}
        currentAccount={currentAccount}
        selectedBranchCode={branch}
        handleChange={setBranch}
      />
    ) : null;
  }

  function renderSubmitButton() {
    return (
      <TransactionalSolidButton
        disabled={isFormInvalid()}
        dataKey="lite-suggestion-form-submit-button"
        handleClick={handleSubmit}
        isLoading={suggestForPurchaseTransactions.get('isAdding')}
      >
        <FormattedMessage id="button_submit" />
      </TransactionalSolidButton>
    );
  }

  return (
    currentAccount && (
      <form className="cp-lite-suggestion-form" onSubmit={handleSubmit}>
        <ModalHeader title={<FormattedMessage id="suggest_for_purchase" />} />
        <ModalContent>
          <BibBriefContainer metadataId={metadataId} />
          {renderSuggestionBox()}
          {holdEnabled && branches.size ? (
            <>
              {renderPlaceHoldCheckbox()}
              {renderBranchSelect()}
            </>
          ) : null}
        </ModalContent>
        <ModalFooter renderPrimaryAction={renderSubmitButton} />
      </form>
    )
  );
}

LiteSuggestionForm.propTypes = {
  currentLibrary: libraryShape.isRequired,
  branches: ImmutablePropTypes.listOf(branchShape).isRequired,
  preferredBranches: ImmutablePropTypes.listOf(branchShape).isRequired,
  currentAccount: accountShape,
  handleClose: PropTypes.func,
  suggestForPurchaseActions: PropTypes.objectOf(PropTypes.func).isRequired,
  suggestForPurchaseTransactions: ImmutablePropTypes.map.isRequired,
  currentUser: userIdentityShape,
  catalogBib: catalogBibShape.isRequired
};
